'use client';

import { useMemo, useRef, useState } from 'react';

import classnames from 'classnames';
import { LuArrowDown, LuArrowUp } from 'react-icons/lu';

import type { BaseComponentProps } from '../../../../base-component.types';
import { Card } from '../../../../card';
import { IconButton } from '../../../../icon-button';
import { Meter } from '../../../../meter';
import type { MeterVariant } from '../../../../meter';

import styles from './SubjectMetrics.module.scss';

export type SubjectMetric = {
  subject: string;
  total: number;
  correct: number;
};

type SortDirection = 'ascending' | 'descending';

type SubjectMetricsProps = BaseComponentProps & {
  subjects: SubjectMetric[];
};

function getVariant(percentage: number): MeterVariant {
  if (percentage >= 70) return 'success';
  if (percentage >= 50) return 'warning';
  return 'danger';
}

function getPercentage(s: SubjectMetric): number {
  return s.total > 0 ? s.correct / s.total : 0;
}

function nextDirection(current: SortDirection): SortDirection {
  return current === 'descending' ? 'ascending' : 'descending';
}

function SubjectMetrics({
  style,
  className,
  subjects,
  testId = 'ui-subject-metrics',
}: SubjectMetricsProps) {
  const [sortDirection, setSortDirection] =
    useState<SortDirection>('descending');
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSortToggle = () => {
    setSortDirection(nextDirection);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  const sortedSubjects = useMemo(() => {
    const sign = sortDirection === 'descending' ? -1 : 1;
    return [...subjects].sort((a, b) => {
      const diff = sign * (getPercentage(a) - getPercentage(b));
      return diff !== 0 ? diff : sign * a.subject.localeCompare(b.subject);
    });
  }, [subjects, sortDirection]);

  const sortIcon =
    sortDirection === 'descending' ? <LuArrowDown /> : <LuArrowUp />;

  return (
    <Card
      style={style}
      className={classnames(styles['main'], className)}
      title="Rendimiento por tema"
      actions={
        <IconButton
          icon={sortIcon}
          size="sm"
          aria-label="Ordenar por aciertos"
          onClick={handleSortToggle}
        />
      }
      testId={testId}
    >
      <div ref={scrollRef} className={styles['scroll']}>
        <div className={styles['list']}>
          {sortedSubjects.map((s) => {
            const percentage =
              s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
            const variant = getVariant(percentage);
            return (
              <div key={s.subject} className={styles['row']}>
                <div className={styles['header']}>
                  <span
                    className={classnames(
                      styles['dot'],
                      styles[`dot--${variant}`],
                    )}
                  />
                  <span className={styles['name']}>{s.subject}</span>
                  <span className={styles['count']}>
                    {s.correct}/{s.total}
                  </span>
                  <span
                    className={classnames(
                      styles['percentage'],
                      styles[`percentage--${variant}`],
                    )}
                  >
                    {percentage}%
                  </span>
                </div>
                <div className={styles['bar']}>
                  <Meter value={percentage} variant={variant} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

export { SubjectMetrics };
